import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Modal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  return <AnimatePresence data-id="element-41">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" data-id="element-42" />

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-43">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full max-w-2xl max-h-[85vh] flex flex-col glass-card rounded-2xl border border-accent-green/20 pointer-events-auto overflow-hidden shadow-2xl" data-id="element-44">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-subtle bg-bg-secondary/50" data-id="element-45">
                <h2 className="text-xl md:text-2xl font-bold text-white" data-id="element-46">
                  {title}
                </h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-text-muted hover:text-white transition-colors" aria-label="Close modal" data-id="element-47">
                  <XIcon className="w-5 h-5" data-id="element-48" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 overflow-y-auto text-text-muted space-y-4 text-sm md:text-base leading-relaxed" data-id="element-49">
                {children}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="w-full py-12 px-4 bg-bg-primary border-t border-border-subtle relative z-10" data-id="element-50">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center space-y-6" data-id="element-51">
          <div className="flex items-center gap-6 text-sm text-text-muted" data-id="element-52">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-accent-green transition-colors duration-200" data-id="element-53">
              Privacy Policy
            </button>
            <span className="w-1 h-1 rounded-full bg-border-subtle" data-id="element-54" />
            <button onClick={() => setActiveModal('terms')} className="hover:text-accent-green transition-colors duration-200" data-id="element-55">
              Terms of Service
            </button>
          </div>

          <p className="text-sm text-text-muted/60 text-center" data-id="element-56">
            © {new Date().getFullYear()} The Anonymous Asset System. All rights
            reserved.
          </p>
        </div>
      </footer>

      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" data-id="element-57">
        <div className="space-y-6" data-id="element-58">
          <section data-id="element-59">
            <h3 className="text-white font-semibold mb-2" data-id="element-60">
              1. Information We Collect
            </h3>
            <p data-id="element-61">
              We collect information you provide directly to us when you make a
              purchase, sign up for our newsletter, or communicate with us. This
              may include your name, email address, payment information, and any
              other details you choose to provide.
            </p>
          </section>
          <section data-id="element-62">
            <h3 className="text-white font-semibold mb-2" data-id="element-63">
              2. How We Use Your Information
            </h3>
            <p data-id="element-64">
              We use the information we collect to process your transactions,
              send you the products you purchased, communicate with you about
              products, services, offers, and provide customer support.
            </p>
          </section>
          <section data-id="element-65">
            <h3 className="text-white font-semibold mb-2" data-id="element-66">
              3. Information Sharing
            </h3>
            <p data-id="element-67">
              We do not sell, trade, or otherwise transfer your personally
              identifiable information to outside parties. This does not include
              trusted third parties who assist us in operating our website,
              conducting our business, or servicing you, so long as those
              parties agree to keep this information confidential.
            </p>
          </section>
          <section data-id="element-68">
            <h3 className="text-white font-semibold mb-2" data-id="element-69">4. Data Security</h3>
            <p data-id="element-70">
              We implement a variety of security measures to maintain the safety
              of your personal information when you place an order or enter,
              submit, or access your personal information.
            </p>
          </section>
          <section data-id="element-71">
            <h3 className="text-white font-semibold mb-2" data-id="element-72">5. Your Rights</h3>
            <p data-id="element-73">
              You have the right to access, correct, or delete your personal
              information. You may also opt out of receiving promotional
              communications from us by following the instructions in those
              messages.
            </p>
          </section>
        </div>
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service" data-id="element-74">
        <div className="space-y-6" data-id="element-75">
          <section data-id="element-76">
            <h3 className="text-white font-semibold mb-2" data-id="element-77">
              1. Acceptance of Terms
            </h3>
            <p data-id="element-78">
              By accessing and using this website and purchasing our products,
              you accept and agree to be bound by the terms and provision of
              this agreement.
            </p>
          </section>
          <section data-id="element-79">
            <h3 className="text-white font-semibold mb-2" data-id="element-80">
              2. Intellectual Property
            </h3>
            <p data-id="element-81">
              All content included on this site, such as text, graphics, logos,
              images, digital downloads, and data compilations is the property
              of The Anonymous Asset System and protected by international
              copyright laws. You may not reproduce, distribute, or transmit any
              part of this publication without prior written permission.
            </p>
          </section>
          <section data-id="element-82">
            <h3 className="text-white font-semibold mb-2" data-id="element-83">
              3. Earnings Disclaimer
            </h3>
            <p data-id="element-84">
              Every effort has been made to accurately represent our products
              and their potential. However, there is no guarantee that you will
              earn any money using the techniques and ideas in these materials.
              Examples in these materials are not to be interpreted as a promise
              or guarantee of earnings. Earning potential is entirely dependent
              on the person using our product, ideas, and techniques.
            </p>
          </section>
          <section data-id="element-85">
            <h3 className="text-white font-semibold mb-2" data-id="element-86">4. Refund Policy</h3>
            <p data-id="element-87">
              Due to the digital nature of this product, all sales are final.
              Once the eBook has been purchased and delivered, no refunds will
              be issued. By completing your purchase, you acknowledge and agree
              that you are buying a digital product and that no refund will be
              provided.
            </p>
          </section>
          <section data-id="element-88">
            <h3 className="text-white font-semibold mb-2" data-id="element-89">
              5. Limitation of Liability
            </h3>
            <p data-id="element-90">
              In no event shall The Anonymous Asset System be liable for any
              direct, indirect, incidental, consequential, special, or exemplary
              damages arising out of or in connection with your access or use of
              or inability to access or use the website and products.
            </p>
          </section>
        </div>
      </Modal>
    </>;
}